import { useData } from '../contexts/DataContext';
import React from 'react';
// Define all available feature flags here
export enum FeatureFlag {
  AD_GENERATION = 'ad_generation',
  SOCIAL_MEDIA_POSTS = 'social_media_posts',
  NEWSLETTER = 'newsletter',
  PRODUCT_DESCRIPTION = 'product_description',
  CATEGORY_DESCRIPTION = 'category_description',
  CHAT = 'chat',
  CUSTOBAR_DATA_INGESTION = 'custobar_data_ingestion',
  ARTICLE_GENERATION = 'article_generation',
}

// Default state for all feature flags (false means disabled by default)
export const DEFAULT_FEATURE_FLAGS: Record<FeatureFlag, boolean> = {
  [FeatureFlag.AD_GENERATION]: false, // Ad generation disabled by default
  [FeatureFlag.SOCIAL_MEDIA_POSTS]: false, // Social media posts disabled by default
  [FeatureFlag.NEWSLETTER]: false, // Newsletter disabled by default
  [FeatureFlag.PRODUCT_DESCRIPTION]: false, // Product description disabled by default
  [FeatureFlag.CATEGORY_DESCRIPTION]: false, // Category description disabled by default
  [FeatureFlag.CHAT]: false, // Chat disabled by default
  [FeatureFlag.CUSTOBAR_DATA_INGESTION]: false, // Custobar data ingestion disabled by default
  [FeatureFlag.ARTICLE_GENERATION]: false, // Article generation disabled by default
};

/**
 * Hook to check if a feature flag is enabled for the current company
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  const { targetCompany, companies } = useData();

  if (!targetCompany) return DEFAULT_FEATURE_FLAGS[flag];

  const company = companies.find((c) => c.id === targetCompany);
  if (!company) return DEFAULT_FEATURE_FLAGS[flag];

  // If feature flag is not set for the company, use the default value
  return company.feature_flags?.[flag] ?? DEFAULT_FEATURE_FLAGS[flag];
}

/**
 * Component wrapper that only renders children if feature flag is enabled
 */
export function FeatureFlagGuard({
  flag,
  children,
  fallback = null,
}: {
  flag: FeatureFlag;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}): JSX.Element | null {
  const isEnabled = useFeatureFlag(flag);
  if (isEnabled) {
    return children as JSX.Element;
  }

  return fallback as JSX.Element | null;
}
